/** Chest tier costs — chests are BOUGHT sequentially, spending coins.
 *
 *  TOKENS rewards are expressed as a **fraction of the entry fee** (decimal
 *  number, e.g. 0.005 = 0.5% of entry). The caller passes the current
 *  entry's USD-micro value and `rollTokens` multiplies in to produce the
 *  reward amount in USD-micro. The server then converts to native-token
 *  raw at consume time via the live oracle, so the payout's USD value
 *  stays stable across price moves AND scales naturally with whatever
 *  entry price the operator runs (testing $0.01 vs prod $1).
 *
 *  Curves grow per tier so the higher-cost chests carry meaningfully
 *  bigger expected value; the legendary jackpot stays scarce thanks to
 *  TOKEN_RARITY_WEIGHTS below (1% legendary). */
const CHEST_TIERS = [
    { tier: 'SALVAGE', cost: 15, name: 'Salvage Crate',
        tokenEntryFraction: { COMMON: 0.005, UNCOMMON: 0.015, RARE: 0.05, LEGENDARY: 0.15 },
        pointsMin: 5, pointsMax: 15 },
    { tier: 'FIELD', cost: 40, name: 'Field Box',
        tokenEntryFraction: { COMMON: 0.008, UNCOMMON: 0.025, RARE: 0.08, LEGENDARY: 0.25 },
        pointsMin: 15, pointsMax: 30 },
    { tier: 'CASE', cost: 80, name: 'Extraction Case',
        tokenEntryFraction: { COMMON: 0.012, UNCOMMON: 0.04, RARE: 0.12, LEGENDARY: 0.35 },
        pointsMin: 30, pointsMax: 60 },
    { tier: 'VAULT', cost: 150, name: 'Vault',
        tokenEntryFraction: { COMMON: 0.018, UNCOMMON: 0.06, RARE: 0.18, LEGENDARY: 0.45 },
        pointsMin: 60, pointsMax: 100 },
    { tier: 'LEVIATHAN', cost: 250, name: 'Leviathan Cache',
        tokenEntryFraction: { COMMON: 0.025, UNCOMMON: 0.08, RARE: 0.25, LEGENDARY: 0.50 },
        pointsMin: 100, pointsMax: 250 },
];
/** Default entry-fee USD-micro used when the caller doesn't pass one
 *  (demo / offline / unit-test paths). $1 = the design reference value
 *  the per-tier fractions were calibrated against. */
const DEFAULT_ENTRY_USD_MICRO = 1_000_000;
/** Rarity weights specifically for TOKEN rewards (75/20/4/1).
 *  Stricter than the hat path's RARITY_WEIGHTS — the chest TOKEN payout
 *  budget is constrained (~10% of the entry-fee pool stat-wise), so the
 *  jackpot legendary needs to be genuinely rare across every tier.
 *  Hats keep their per-tier scaling (more legendary at LEVIATHAN). */
const TOKEN_RARITY_WEIGHTS = {
    COMMON: 75,
    UNCOMMON: 20,
    RARE: 4,
    LEGENDARY: 1,
};
/** Public view of chest tiers for HUD widgets — name + cost + cumulative threshold.
 *  Shared so client + server agree on what "next chest" means. */
export const CHEST_TIER_VIEW = (() => {
    let acc = 0;
    return CHEST_TIERS.map(t => {
        acc += t.cost;
        return { tier: t.tier, name: t.name, cost: t.cost, cumulativeThreshold: acc };
    });
})();
/** Rarity weights per tier index — legendary is truly rare */
const RARITY_WEIGHTS = {
    0: { COMMON: 80, UNCOMMON: 18, RARE: 2, LEGENDARY: 0 },
    1: { COMMON: 65, UNCOMMON: 27, RARE: 7, LEGENDARY: 1 },
    2: { COMMON: 50, UNCOMMON: 32, RARE: 15, LEGENDARY: 3 },
    3: { COMMON: 30, UNCOMMON: 35, RARE: 28, LEGENDARY: 7 },
    4: { COMMON: 15, UNCOMMON: 28, RARE: 42, LEGENDARY: 15 },
};
/** Hat pool — chest-droppable hats only. 'cap' and 'none' are baseline
 *  unlocks every player has from the start, so they're excluded here.
 *  IDs match SkinSelectScene HAT_OPTIONS (bare ids, no `hat_` prefix). */
const HAT_POOL = [
    // Common
    { id: 'cowboy', name: 'Cowboy Hat', rarity: 'COMMON' },
    // Uncommon
    { id: 'party', name: 'Party Hat', rarity: 'UNCOMMON' },
    { id: 'viking', name: 'Viking Helmet', rarity: 'UNCOMMON' },
    // Rare
    { id: 'wizard', name: 'Wizard Hat', rarity: 'RARE' },
    { id: 'knight', name: 'Knight Helmet', rarity: 'RARE' },
    // Legendary
    { id: 'crown', name: 'Royal Crown', rarity: 'LEGENDARY' },
    { id: 'pepper', name: 'Pepper', rarity: 'LEGENDARY' },
];
function rollRarity(tierIndex) {
    const weights = RARITY_WEIGHTS[tierIndex] ?? RARITY_WEIGHTS[0];
    const total = Object.values(weights).reduce((a, b) => a + b, 0);
    let roll = Math.random() * total;
    for (const [rarity, weight] of Object.entries(weights)) {
        roll -= weight;
        if (roll <= 0)
            return rarity;
    }
    return 'COMMON';
}
/** Roll a hat from the requested rarity, skipping any the player already
 *  owns. Returns null if every hat in that rarity is already owned (the
 *  caller should then fallback to a different reward type). */
function rollHat(rarity, ownedHatIds) {
    let pool = HAT_POOL.filter(h => h.rarity === rarity);
    if (ownedHatIds) {
        pool = pool.filter(h => !ownedHatIds.has(h.id));
    }
    if (pool.length === 0)
        return null;
    const item = pool[Math.floor(Math.random() * pool.length)];
    return { type: 'HAT', rarity, id: item.id, name: item.name, description: 'Hat', slot: 'HAT' };
}
function rollTokenRarity() {
    const total = TOKEN_RARITY_WEIGHTS.COMMON + TOKEN_RARITY_WEIGHTS.UNCOMMON
        + TOKEN_RARITY_WEIGHTS.RARE + TOKEN_RARITY_WEIGHTS.LEGENDARY;
    let roll = Math.random() * total;
    for (const r of ['COMMON', 'UNCOMMON', 'RARE', 'LEGENDARY']) {
        roll -= TOKEN_RARITY_WEIGHTS[r];
        if (roll <= 0)
            return r;
    }
    return 'COMMON';
}
/** Format a USD-micro integer for human display ("$0.05"). */
function formatUsdMicro(usdMicro) {
    const usd = usdMicro / 1_000_000;
    return usd >= 1 ? `$${usd.toFixed(2)}`
        : usd >= 0.01 ? `$${usd.toFixed(2)}`
            : `$${usd.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}`;
}
function rollTokens(tierConfig, entryUsdMicro) {
    const rarity = rollTokenRarity();
    const fraction = tierConfig.tokenEntryFraction[rarity];
    // Round to integer USD-micro — sub-micro precision is meaningless and
    // would just compound floating-point noise downstream in the oracle
    // conversion.
    const usdMicro = Math.max(1, Math.round(fraction * entryUsdMicro));
    const label = formatUsdMicro(usdMicro);
    return {
        type: 'TOKENS',
        rarity,
        id: 'tokens',
        name: `${label} Reward`,
        description: 'Token reward (paid in native game token at current rate)',
        amount: usdMicro,
    };
}
function rollPoints(tierConfig) {
    const amount = tierConfig.pointsMin + Math.floor(Math.random() * (tierConfig.pointsMax - tierConfig.pointsMin + 1));
    return {
        type: 'POINTS',
        rarity: 'COMMON',
        id: 'points',
        name: `${amount} Points`,
        description: 'Leaderboard Points',
        amount,
    };
}
/** Generate a single chest's reward — 1 of 3 types: points, tokens, or hat.
 *  If the rolled hat slot would land on something the player already owns,
 *  fall back to leaderboard points so the chest still feels worth opening. */
function generateChest(tierIndex, entryUsdMicro, ownedHatIds) {
    const tierConfig = CHEST_TIERS[tierIndex];
    const rewards = [];
    // Roll reward type: 35% points, 35% tokens, 30% hat
    const roll = Math.random();
    if (roll < 0.35) {
        rewards.push(rollPoints(tierConfig));
    }
    else if (roll < 0.70) {
        rewards.push(rollTokens(tierConfig, entryUsdMicro));
    }
    else {
        // Hat — rarity based on chest tier. Try the rolled rarity first; if
        // every hat at that rarity is already owned, walk other rarities so a
        // player with most-but-not-all of a tier can still get a new one.
        // Only after exhausting every unowned hat do we fallback to points.
        const rarities = ['LEGENDARY', 'RARE', 'UNCOMMON', 'COMMON'];
        const primary = rollRarity(tierIndex);
        const tryOrder = [primary, ...rarities.filter(r => r !== primary)];
        let hat = null;
        for (const r of tryOrder) {
            hat = rollHat(r, ownedHatIds);
            if (hat)
                break;
        }
        if (hat) {
            rewards.push(hat);
        }
        else {
            // Player owns every hat — give points instead so the chest stays useful
            rewards.push(rollPoints(tierConfig));
        }
    }
    return {
        tier: tierConfig.tier,
        tierIndex,
        rewards,
        leaderboardPoints: tierConfig.pointsMin, // base points from chest itself
    };
}
/** Calculate extraction result — chests are BOUGHT sequentially, spending coins.
 *
 *  `ownedHatIds` is the player's current hat inventory; chests will not
 *  drop a hat the player already owns. If passed, the running set is
 *  mutated as new hats drop so multi-chest extractions don't roll the
 *  same hat twice in a row.
 *
 *  `entryUsdMicro` scales the TOKEN rewards relative to the buy-in so
 *  the per-snake payout budget stays the same fraction of entry no
 *  matter what BUYIN_USD_MICRO the operator runs (testing $0.01 vs
 *  prod $1). Defaults to $1 for demo/offline callers.
 *
 *  `chestsAlreadyOpened` + `coinsCarryover` implement per-life chest
 *  progression: each chest tier opens at most once per snake (per
 *  payment), and coins not spent in a previous extraction roll over
 *  into the next. The caller is responsible for tracking these across
 *  re-entries and resetting them on a new life. */
export function calculateExtractionResult(coinsBanked, coinsCollected, kills, chainMax, perkUsed, ownedHatIds, entryUsdMicro = DEFAULT_ENTRY_USD_MICRO, chestsAlreadyOpened = 0, coinsCarryover = 0) {
    const chests = [];
    let remaining = coinsBanked + Math.max(0, coinsCarryover);
    let spent = 0;
    // Local clone so the caller's set isn't mutated mid-roll
    const owned = ownedHatIds ? new Set(ownedHatIds) : undefined;
    // Buy chests in order starting from the next un-opened tier. Earlier
    // tiers already paid for in prior extractions of this life are
    // skipped — you can't re-roll the cheap SALVAGE chest by extracting
    // twice with small banks.
    const startTier = Math.max(0, Math.min(chestsAlreadyOpened, CHEST_TIERS.length));
    for (let i = startTier; i < CHEST_TIERS.length; i++) {
        if (remaining >= CHEST_TIERS[i].cost) {
            remaining -= CHEST_TIERS[i].cost;
            spent += CHEST_TIERS[i].cost;
            const chest = generateChest(i, entryUsdMicro, owned);
            // Track newly-rolled hats so subsequent chests in this same extract
            // don't roll the same one again
            if (owned) {
                for (const r of chest.rewards) {
                    if (r.type === 'HAT')
                        owned.add(r.id);
                }
            }
            chests.push(chest);
        }
        else {
            break; // Can't afford the next tier
        }
    }
    // Calculate next chest info (relative to total tiers opened, not just
    // this extraction's count).
    let nextThreshold = 0;
    let coinsToNext = 0;
    const totalOpened = startTier + chests.length;
    if (totalOpened < CHEST_TIERS.length) {
        nextThreshold = CHEST_TIERS[totalOpened].cost;
        coinsToNext = CHEST_TIERS[totalOpened].cost - remaining;
    }
    const totalLeaderboardPoints = chests.reduce((sum, c) => sum + c.leaderboardPoints, 0);
    return {
        coinsCollected,
        coinsBanked,
        coinsRemaining: remaining,
        coinsSpent: spent,
        kills,
        chainMax,
        perkUsed,
        chests,
        nextChestThreshold: nextThreshold,
        coinsToNextChest: Math.max(0, coinsToNext),
        totalLeaderboardPoints,
    };
}
/** Get chest tier display info */
export function getChestTierInfo(tier) {
    switch (tier) {
        case 'SALVAGE': return { name: 'Salvage Crate', color: 0x8B6914 };
        case 'FIELD': return { name: 'Field Box', color: 0xC0C0C0 };
        case 'CASE': return { name: 'Extraction Case', color: 0xFFD700 };
        case 'VAULT': return { name: 'Vault', color: 0xFF6B00 };
        case 'LEVIATHAN': return { name: 'Leviathan Cache', color: 0xFF00FF };
    }
}
/** Get rarity display info */
export function getRarityInfo(rarity) {
    switch (rarity) {
        case 'COMMON': return { label: 'Common', color: 0x888899 };
        case 'UNCOMMON': return { label: 'Uncommon', color: 0x22CC44 };
        case 'RARE': return { label: 'Rare', color: 0x4488FF };
        case 'LEGENDARY': return { label: 'LEGENDARY', color: 0xFFD700 };
    }
}
//# sourceMappingURL=chest-system.js.map